import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, Stepper } from '../shared';
import { formatNum, parsePrice } from '../../utils/helpers';

const STORAGE_KEY = 'enchant_calc_last';

// Enchant chances per level (null = unknown)
const ENCHANT_CHANCES = [
  100, 100, 100,        // +0 to +3 (safe)
  90, 65, 55, 40,       // +4 to +7
  35, null, null,       // +8 to +10
  null, null, null,     // +11 to +13
  null, null, null,     // +14 to +16
  null, null, null, null // +17 to +20 (unknown)
];

function calculateEnchantExpected(chances, target) {
  // Calculate expected attempts using regular scrolls (fail = lose item, restart from +0)
  const E = [0]; // E[n] = expected attempts to reach +n from +0
  let regularAttempts = null;
  let itemsLost = null;
  let hasUnknownRegular = false;
  
  let probSuccess = 1;
  
  for (let lvl = 0; lvl < target; lvl++) {
    const chance = chances[lvl];
    if (chance === null || chance === undefined) {
      hasUnknownRegular = true;
      break;
    }
    
    const p = chance / 100;
    if (lvl < 3) {
      E.push(E[lvl] + 1);
    } else {
      E.push((E[lvl] + 1) / p);
      probSuccess *= p;
    }
  }
  
  if (!hasUnknownRegular) {
    regularAttempts = E[target];
    const expectedItems = 1 / probSuccess;
    itemsLost = expectedItems - 1;
  }
  
  // Calculate expected attempts using blessed scrolls (goes to +4 on fail)
  let blessAttempts = null;
  let hasUnknownBless = false;
  
  if (target <= 4) {
    blessAttempts = target;
  } else {
    for (let lvl = 0; lvl < target; lvl++) {
      if (chances[lvl] === null || chances[lvl] === undefined) {
        hasUnknownBless = true;
        break;
      }
    }
    
    if (!hasUnknownBless) {
      let B = 0;
      
      for (let lvl = 4; lvl < target; lvl++) {
        const chance = chances[lvl];
        const p = chance / 100;
        B = (B + 1) / p;
      }
      
      blessAttempts = 4 + B;
    }
  }
  
  return { regularAttempts, itemsLost, blessAttempts };
}

export function EnchantPanel({ active }) {
  const { t, lang } = useLanguage();
  
  const [target, setTarget] = useState(0);
  const [itemPrice, setItemPrice] = useState('');
  const [regularScroll, setRegularScroll] = useState('');
  const [blessScroll, setBlessScroll] = useState('');
  const [showBreakdown, setShowBreakdown] = useState(false);

  // Restore state
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const state = JSON.parse(saved);
        if (state.target !== undefined) setTarget(state.target);
        if (state.itemPrice) setItemPrice(state.itemPrice);
        if (state.regularScroll) setRegularScroll(state.regularScroll);
        if (state.blessScroll) setBlessScroll(state.blessScroll);
      }
    } catch (e) {}
  }, []);

  // Save state
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        target,
        itemPrice,
        regularScroll,
        blessScroll
      }));
    } catch (e) {}
  }, [target, itemPrice, regularScroll, blessScroll]);

  const expected = useMemo(() => {
    return calculateEnchantExpected(ENCHANT_CHANCES, target);
  }, [target]);

  const chancesGrid = useMemo(() => {
    return Array.from({ length: target }, (_, i) => {
      const chance = ENCHANT_CHANCES[i];
      const isSafe = i < 3;
      const isUnknown = chance === null;
      
      return {
        level: i,
        chance,
        isSafe,
        isUnknown
      };
    });
  }, [target]);

  const costComparison = useMemo(() => {
    const item = parsePrice(itemPrice);
    const regular = parsePrice(regularScroll);
    const blessed = parsePrice(blessScroll);
    
    if (isNaN(item) || isNaN(regular) || isNaN(blessed) ||
        expected.regularAttempts === null || expected.blessAttempts === null) {
      return null;
    }
    
    const regularScrollsCost = expected.regularAttempts * regular + expected.itemsLost * item;
    const blessScrollsCost = 3 * regular + (expected.blessAttempts - 3) * blessed;
    
    return {
      regularCost: regularScrollsCost,
      blessCost: blessScrollsCost,
      isBlessedBetter: blessScrollsCost < regularScrollsCost
    };
  }, [itemPrice, regularScroll, blessScroll, expected]);

  const breakdown = useMemo(() => {
    if (!costComparison) return [];
    
    const item = parsePrice(itemPrice);
    const regular = parsePrice(regularScroll);
    const blessed = parsePrice(blessScroll);
    
    const rows = [];
    for (let lvl = 4; lvl <= target; lvl++) {
      const stepExp = calculateEnchantExpected(ENCHANT_CHANCES, lvl);
      if (stepExp.regularAttempts === null || stepExp.blessAttempts === null) continue;
      
      const stepRegCost = stepExp.regularAttempts * regular + stepExp.itemsLost * item;
      const stepBlessCost = 3 * regular + (stepExp.blessAttempts - 3) * blessed;
      
      rows.push({
        level: lvl,
        regularAttempts: Math.ceil(stepExp.regularAttempts),
        blessAttempts: Math.ceil(stepExp.blessAttempts),
        regularCost: stepRegCost,
        blessCost: stepBlessCost,
        regWins: stepRegCost < stepBlessCost,
        blessWins: stepBlessCost < stepRegCost,
        isLast: lvl === target
      });
    }
    
    return rows;
  }, [costComparison, target, itemPrice, regularScroll, blessScroll]);

  const showDetails = target > 0;

  if (!active) return null;

  return (
    <div className="calculator-panel active">
      <Card title={t('configuration')}>
        <div className="input-group">
          <label>{t('targetEnchantLevel')}</label>
          <Stepper
            value={target}
            onChange={setTarget}
            min={0}
            max={8}
          />
        </div>
      </Card>

      {showDetails && (
        <>
          <Card title={t('enchantChancesTitle')}>
            <div className="enchant-chances-grid">
              {chancesGrid.map(({ level, chance, isSafe, isUnknown }) => (
                <div
                  key={level}
                  className={`enchant-chance-item ${isSafe ? 'safe' : ''} ${isUnknown ? 'unknown' : ''}`}
                >
                  <div className="enchant-chance-level">+{level}→+{level + 1}</div>
                  <div className="enchant-chance-value">{isUnknown ? '?' : `${chance}%`}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card title={t('expectedAttemptsTitle')}>
            <div className="row">
              <span className="row-label">{t('regularScrolls')}</span>
              <span className="row-value">
                {expected.regularAttempts !== null ? `~${Math.ceil(expected.regularAttempts)}` : '?'}
              </span>
            </div>
            <div className="row">
              <span className="row-label">↳ {t('itemsNeeded')}</span>
              <span className="row-value danger">
                {expected.itemsLost !== null ? `~${(expected.itemsLost + 1).toFixed(1)}` : '?'}
              </span>
            </div>
            <div className="row">
              <span className="row-label">{t('blessedScrolls')}</span>
              <span className="row-value">
                {expected.blessAttempts !== null ? `~${Math.ceil(expected.blessAttempts)}` : '?'}
              </span>
            </div>
          </Card>

          <Card title={t('prices')}>
            <div className="input-group">
              <label>{t('itemPrice')}</label>
              <div className="input-wrapper">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="19500"
                  value={itemPrice}
                  onChange={(e) => setItemPrice(e.target.value.replace(/[^0-9]/g, ''))}
                  onFocus={(e) => {
                    const input = e.target;
                    requestAnimationFrame(() => input.setSelectionRange(input.value.length, input.value.length));
                  }}
                />
                {itemPrice && (
                  <button className="clear-btn visible" onClick={() => setItemPrice('')}>×</button>
                )}
              </div>
            </div>
            <div className="input-group">
              <label>{t('regularScrollPrice')}</label>
              <div className="input-wrapper">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="1500"
                  value={regularScroll}
                  onChange={(e) => setRegularScroll(e.target.value.replace(/[^0-9]/g, ''))}
                  onFocus={(e) => {
                    const input = e.target;
                    requestAnimationFrame(() => input.setSelectionRange(input.value.length, input.value.length));
                  }}
                />
                {regularScroll && (
                  <button className="clear-btn visible" onClick={() => setRegularScroll('')}>×</button>
                )}
              </div>
            </div>
            <div className="input-group">
              <label>{t('blessedScrollPrice')}</label>
              <div className="input-wrapper">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="15000"
                  value={blessScroll}
                  onChange={(e) => setBlessScroll(e.target.value.replace(/[^0-9]/g, ''))}
                  onFocus={(e) => {
                    const input = e.target;
                    requestAnimationFrame(() => input.setSelectionRange(input.value.length, input.value.length));
                  }}
                />
                {blessScroll && (
                  <button className="clear-btn visible" onClick={() => setBlessScroll('')}>×</button>
                )}
              </div>
            </div>
          </Card>

          {costComparison && (
            <Card title={t('expectedCostComparison')}>
              <div className="row">
                <span className="row-label">{t('regularCost')}</span>
                <span className="row-value">~{formatNum(Math.ceil(costComparison.regularCost))}</span>
              </div>
              <div className="row no-border">
                <span className="row-label">{t('blessedCost')}</span>
                <span className="row-value">~{formatNum(Math.ceil(costComparison.blessCost))}</span>
              </div>
              <div className="row highlight">
                <span className="row-label">{t('recommendation')}</span>
                <span className={`row-value ${costComparison.isBlessedBetter ? 'positive' : ''}`}>
                  {costComparison.isBlessedBetter ? `✅ ${t('recBlessed')}` : `⚠️ ${t('recRegular')}`}
                </span>
              </div>
              
              <div
                className="step-breakdown-toggle"
                onClick={() => setShowBreakdown(!showBreakdown)}
              >
                {showBreakdown ? t('hide') : t('viewCostPerLevel')}
              </div>
              
              {showBreakdown && (
                <div className="step-breakdown">
                  <div className="step-breakdown-row header">
                    <span className="level">{t('levelCol')}</span>
                    <span className="attempts">{t('scrollsCol')}</span>
                    <span className="regular">{t('regularCol')}</span>
                    <span className="blessed">{t('blessedCol')}</span>
                  </div>
                  {breakdown.map(row => (
                    <div key={row.level} className={`step-breakdown-row ${row.isLast ? 'last' : ''}`}>
                      <span className="level">+{row.level}</span>
                      <span className="attempts">{row.regularAttempts} / {row.blessAttempts}</span>
                      <span className={`regular ${row.regWins ? 'winner' : ''}`}>
                        ~{formatNum(Math.ceil(row.regularCost))}
                      </span>
                      <span className={`blessed ${row.blessWins ? 'winner' : ''}`}>
                        ~{formatNum(Math.ceil(row.blessCost))}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
